import styled, { css, keyframes } from 'styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { FileProps } from './DropFile';
import { useFileIntersectionObserver } from './FileIntersectionObserverProvider';

type Props<T extends boolean> = {
  readonly isImage: T;
  readonly file: FileProps;
};

type StateType<T extends boolean> = T extends true
  ? HTMLImageElement
  : HTMLVideoElement;

type StyledMediaProps = {
  readonly loaded: boolean;
};

const Container = styled.div`
  max-height: 150px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Loader = styled.div`
  border: ${(props) => `16px solid ${props.theme.color('#239AE7')}`};
  border-radius: 50%;
  border-top: ${(props) => `16px solid ${props.theme.color('#158AD6')}`};
  width: 80px;
  height: 80px;
  animation: ${spin} 2s linear infinite;
  position: absolute;
`;

const StyledMedia = css<StyledMediaProps>`
  opacity: ${(props) => (props.loaded ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
  width: 100%;
  height: 100%;
`;

const StyledVideo = styled.video`
  ${StyledMedia}
`;

const StyledImage = styled.img`
  ${StyledMedia}
`;

export const LazyLoadingFile = <T extends boolean>({
  isImage,
  file,
}: Props<T>) => {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<StateType<T>>(null);
  const observer = useFileIntersectionObserver();

  const handleOnload = () => {
    setLoaded(true);
  };

  useEffect(() => {
    const media = ref.current;
    if (observer && media) {
      observer.observe(media);
      if (isImage) {
        media.onload = handleOnload;
      }
    }

    return () => {
      if (observer && media) {
        observer.unobserve(media);
      }
    };
  }, [observer]);

  return (
    <Container>
      {!loaded && <Loader />}
      {isImage ? (
        <StyledImage
          loading="lazy"
          alt={file.name}
          ref={ref as React.RefObject<HTMLImageElement>}
          data-src={file?.asset}
          loaded={loaded}
        />
      ) : (
        <StyledVideo
          ref={ref as React.RefObject<HTMLVideoElement>}
          loaded={loaded}
          data-src={file?.asset}
          onCanPlay={handleOnload}
          autoPlay
          muted
          loop
        >
          <source type={file.contentType} />
          Your browser does not support the video tag.
        </StyledVideo>
      )}
    </Container>
  );
};
